import axios from 'axios';
import {
  TrainingPackage,
  TrainingPackageCreate,
  TrainingPackageUpdate,
  ClientPackage,
  ClientPackageCreate,
  ClientPackageUpdate,
  PackageUsageStats
} from '../types';

// Базовая конфигурация для axios
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Создаем экземпляр axios с базовой конфигурацией
const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Добавляем interceptor для автоматического добавления токена
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('trainer_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Добавляем interceptor для обработки ошибок 401 с попыткой обновления токена
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem('trainer_refresh_token');
      if (refreshToken) {
        try {
          const refreshClient = axios.create({
            baseURL: `${API_BASE_URL}/api`,
            headers: { 'Content-Type': 'application/json' }
          });

          const response = await refreshClient.post('/auth/refresh', {
            refresh_token: refreshToken
          });

          const data = response.data;
          localStorage.setItem('trainer_token', data.access_token);
          localStorage.setItem('trainer_refresh_token', data.refresh_token);

          // Повторяем оригинальный запрос с новым токеном
          originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
          return api.request(originalRequest);
        } catch (refreshError) {
          // Refresh не удался, очищаем токены и перенаправляем на логин
          localStorage.removeItem('trainer_token');
          localStorage.removeItem('trainer_refresh_token');
          if (!window.location.pathname.includes('/login')) {
            window.location.href = '/login';
          }
        }
      }
    }

    return Promise.reject(error);
  }
);

// Эндпоинты для пакетов тренировок
export const packagesApi = {
  // Получить все пакеты тренера
  getPackages: async (activeOnly: boolean = true): Promise<TrainingPackage[]> => {
    const response = await api.get('/packages', {
      params: { active_only: activeOnly }
    });
    return response.data;
  },

  // Получить конкретный пакет
  getPackage: async (packageId: number): Promise<TrainingPackage> => {
    const response = await api.get(`/packages/${packageId}`);
    return response.data;
  },

  // Создать пакет
  createPackage: async (data: TrainingPackageCreate): Promise<TrainingPackage> => {
    const response = await api.post('/packages', data);
    return response.data;
  },

  // Обновить пакет
  updatePackage: async (packageId: number, data: TrainingPackageUpdate): Promise<TrainingPackage> => {
    const response = await api.put(`/packages/${packageId}`, data);
    return response.data;
  },

  // Удалить пакет
  deletePackage: async (packageId: number): Promise<void> => {
    await api.delete(`/packages/${packageId}`);
  },

  // Назначить пакет клиенту
  assignPackageToClient: async (packageId: number, data: ClientPackageCreate): Promise<ClientPackage> => {
    const response = await api.post(`/packages/${packageId}/assign`, data);
    return response.data;
  },

  // Получить пакеты клиента
  getClientPackages: async (clientId: number, activeOnly: boolean = true): Promise<ClientPackage[]> => {
    const response = await api.get(`/clients/${clientId}/packages`, {
      params: { active_only: activeOnly }
    });
    return response.data;
  },

  // Получить историю всех пакетов клиента
  getClientPackagesHistory: async (clientId: number, limit: number = 10): Promise<ClientPackage[]> => {
    const response = await api.get(`/clients/${clientId}/packages/history`, {
      params: { limit }
    });
    return response.data;
  },

  // Обновить пакет клиента
  updateClientPackage: async (clientPackageId: number, data: ClientPackageUpdate): Promise<ClientPackage> => {
    const response = await api.put(`/client-packages/${clientPackageId}`, data);
    return response.data;
  },

  // Использовать сессию из пакета
  usePackageSession: async (clientPackageId: number): Promise<{ message: string; sessions_used: number; sessions_remaining: number }> => {
    const response = await api.post(`/client-packages/${clientPackageId}/use-session`);
    return response.data;
  },

  // Отвязать пакет от клиента
  unassignPackageFromClient: async (clientPackageId: number): Promise<{ message: string; deleted_workout_links: number; sessions_consumed_total: number }> => {
    const response = await api.delete(`/client-packages/${clientPackageId}`);
    return response.data;
  },

  // Получить статистику использования пакета
  getPackageUsageStats: async (clientPackageId: number): Promise<PackageUsageStats[]> => {
    const response = await api.get(`/client-packages/${clientPackageId}/usage-stats`);
    return response.data;
  }
};